import { fetchWithRetry, responseError } from './apiError';
import { tokenService } from './tokenService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const REPLAY_BAR_SETTINGS_CACHE_MS = 30000;

export interface ReplayBarSettings {
  enabled: boolean;
}

interface ReplayBarSettingsCache {
  authToken: string | null;
  data: ReplayBarSettings;
  fetchedAt: number;
  revision: number;
}

interface ReplayBarSettingsRequest {
  authToken: string | null;
  id: symbol;
  promise: Promise<ReplayBarSettings>;
}

let settingsCache: ReplayBarSettingsCache | null = null;
let settingsRequest: ReplayBarSettingsRequest | null = null;
let settingsRevision = 0;

const fetchWithAuth = (options?: globalThis.RequestInit): Promise<globalThis.Response> => {
  const token = tokenService.getAccessToken();

  return fetchWithRetry(`${API_URL}/api/v1/admin/settings/replay-bar`, {
    ...options,
    headers: { ...options?.headers, Authorization: `Bearer ${token}` },
  });
};

const readResponse = async (response: globalThis.Response): Promise<ReplayBarSettings> => {
  if (!response.ok) throw await responseError(response);

  return response.json();
};

const cacheFor = (authToken: string | null): ReplayBarSettingsCache | null => (
  settingsCache?.authToken === authToken ? settingsCache : null
);

const storeSettings = (
  authToken: string | null,
  data: ReplayBarSettings,
  revision: number,
): void => {
  if (tokenService.getAccessToken() !== authToken) return;

  settingsCache = { authToken, data, fetchedAt: Date.now(), revision };
};

const loadSettings = (
  authToken: string | null,
  revision: number,
): Promise<ReplayBarSettings> => {
  const id = Symbol('replay-bar-settings-request');
  const promise = fetchWithAuth()
    .then(readResponse)
    .then((data) => {
      if (settingsRevision === revision) storeSettings(authToken, data, revision);

      return data;
    })
    .finally(() => {
      if (settingsRequest?.id === id) settingsRequest = null;
    });

  settingsRequest = { authToken, id, promise };

  return promise;
};

const getReplayBarSettings = (): Promise<ReplayBarSettings> => {
  const authToken = tokenService.getAccessToken();
  const cached = cacheFor(authToken);
  const cacheFresh = cached
    && cached.revision === settingsRevision
    && Date.now() - cached.fetchedAt < REPLAY_BAR_SETTINGS_CACHE_MS;

  if (cacheFresh) return Promise.resolve(cached.data);
  if (settingsRequest?.authToken === authToken) return settingsRequest.promise;

  return loadSettings(authToken, settingsRevision);
};

const setReplayBarSettings = async (enabled: boolean): Promise<ReplayBarSettings> => {
  const authToken = tokenService.getAccessToken();
  const response = await fetchWithAuth({
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ enabled }),
  });
  const data = await readResponse(response);

  settingsRevision += 1;
  storeSettings(authToken, data, settingsRevision);

  return data;
};

export const replayBarSettingsService = {
  getReplayBarSettings,
  setReplayBarSettings,
};
